import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, LogOut, CalendarCheck, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Shared top nav for /marketplace, /coach/:slug, /saved, /compare, /coach-dashboard.
// Handles the Google-authenticated user chip + sign-in CTA in one place.
export default function MarketplaceNav() {
  const { pathname } = useLocation();
  const onMarket = pathname.startsWith("/marketplace");
  const onSaved = pathname.startsWith("/saved");
  const onSessions = pathname.startsWith("/my-sessions");
  const onCoachDash = pathname.startsWith("/coach-dashboard");
  const { user, coachStatus, openSignIn, signOut, loading } = useAuth();
  const isApprovedCoach = !!(coachStatus?.is_coach && coachStatus?.is_approved);

  const initial = (user?.name || user?.email || "?").trim().slice(0, 1).toUpperCase();

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="sticky top-0 inset-x-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200/70"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="market-nav-logo"
          className="flex items-center gap-2 group"
        >
          <span className="relative inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#0f172a] overflow-hidden">
            <span className="absolute inset-0 bg-[#2563EB] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
            <span className="relative text-white font-display font-bold text-sm tracking-tighter">C</span>
          </span>
          <span className="font-display font-semibold text-[17px] tracking-tighter text-slate-900">
            coachly<span className="text-[#10B981]">.</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-[13.5px] text-slate-600">
          <Link to="/" className="hover-underline hover:text-slate-900 transition-colors">Home</Link>
          <Link
            to="/marketplace"
            data-testid="market-nav-marketplace"
            className={`hover-underline transition-colors ${onMarket ? "text-slate-900 font-medium" : "hover:text-slate-900"}`}
          >
            Marketplace
          </Link>
          {user && (
            <Link
              to="/saved"
              data-testid="market-nav-saved"
              className={`inline-flex items-center gap-1.5 hover-underline transition-colors ${onSaved ? "text-slate-900 font-medium" : "hover:text-slate-900"}`}
            >
              <Heart className="w-3.5 h-3.5" strokeWidth={2.4} />
              Saved
            </Link>
          )}
          {user && (
            <Link
              to="/my-sessions"
              data-testid="market-nav-sessions"
              className={`inline-flex items-center gap-1.5 hover-underline transition-colors ${onSessions ? "text-slate-900 font-medium" : "hover:text-slate-900"}`}
            >
              <CalendarCheck className="w-3.5 h-3.5" strokeWidth={2.4} />
              My sessions
            </Link>
          )}
          {isApprovedCoach && (
            <Link
              to="/coach-dashboard"
              data-testid="market-nav-coach-dashboard"
              className={`inline-flex items-center gap-1.5 hover-underline transition-colors ${onCoachDash ? "text-slate-900 font-medium" : "hover:text-slate-900"}`}
            >
              <LayoutDashboard className="w-3.5 h-3.5" strokeWidth={2.4} />
              Coach dashboard
            </Link>
          )}
        </nav>

        {loading ? (
          <div className="w-24 h-10" />
        ) : user ? (
          <div className="flex items-center gap-2" data-testid="market-nav-user">
            {isApprovedCoach && (
              <Link
                to="/coach-dashboard"
                data-testid="market-nav-coach-dashboard-cta"
                className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white text-[12.5px] font-semibold transition-colors"
                title="Go to your coach dashboard"
              >
                <LayoutDashboard className="w-3.5 h-3.5" strokeWidth={2.4} />
                <span className="hidden sm:inline">Dashboard</span>
              </Link>
            )}
            <div className="hidden sm:flex items-center gap-2 h-10 pl-1.5 pr-3 rounded-full bg-slate-50 border border-slate-200">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
              ) : (
                <span className="w-7 h-7 rounded-full bg-[#2563EB] text-white grid place-items-center text-[11px] font-semibold">{initial}</span>
              )}
              <span data-testid="market-nav-user-email" className="text-[12.5px] font-medium text-slate-700 max-w-[160px] truncate">
                {user.name || user.email}
              </span>
            </div>
            <button
              onClick={signOut}
              data-testid="market-nav-signout"
              className="h-10 w-10 sm:w-auto sm:px-3 rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-50 inline-flex items-center justify-center gap-1.5 text-[12.5px] font-medium"
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => openSignIn("Sign in to save coaches, message them, and book sessions.")}
            data-testid="market-nav-signin"
            className="btn-primary h-10 px-4 rounded-full text-sm font-medium inline-flex items-center gap-1.5"
          >
            Sign in
            <span aria-hidden>→</span>
          </button>
        )}
      </div>
    </motion.header>
  );
}
